import React from 'react';
import { CheckCircle, XCircle, Clock, FileText } from 'lucide-react';

const BulkUploadModal = ({ isOpen, files, onClose, isComplete }) => {
  if (!isOpen) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'processing':
        return <div className="animate-spin h-5 w-5 border-2 border-blue-800 border-t-transparent rounded-full" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status, result) => {
    switch (status) {
      case 'completed':
        return result ? `${result.status} (${result.errors === -1 ? 'INVALID' : result.errors} errors)` : 'Completed';
      case 'error':
        return result || 'Failed to process';
      case 'processing':
        return 'Processing...';
      default:
        return 'Waiting...';
    }
  };

  const completedFiles = files.filter(f => f.status === 'completed');
  const errorFiles = files.filter(f => f.status === 'error');

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-screen w-screen z-50 flex items-start justify-center" style={{ margin: 0, padding: 0 }}>
      <div className="relative mt-10 p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Bulk SOF Upload Progress
            </h3>
            {isComplete && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6 text-red-500 "  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Progress Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-blue-800">{files.length}</p>
                <p className="text-sm text-gray-600">Total Files</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{completedFiles.length}</p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{errorFiles.length}</p>
                <p className="text-sm text-gray-600">Failed</p>
              </div>
            </div>
          </div>

          {/* File List */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {files.map((file, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  file.status === 'completed' 
                    ? 'bg-green-50 border-green-200' 
                    : file.status === 'error'
                    ? 'bg-red-50 border-red-200'
                    : file.status === 'processing'
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-600">
                      {getStatusText(file.status, file.result)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  {getStatusIcon(file.status)}
                </div>
              </div>
            ))}
          </div>

          {/* Summary when complete */}
          {isComplete && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Upload Complete!</h4>
              <p className="text-blue-800 text-sm">
                {completedFiles.length > 0 && 
                  `${completedFiles.length} file${completedFiles.length > 1 ? 's' : ''} successfully processed and added to Recent Validation History.`
                }
                {errorFiles.length > 0 && 
                  ` ${errorFiles.length} file${errorFiles.length > 1 ? 's' : ''} failed to process.`
                }
              </p>
              {completedFiles.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-blue-900 mb-1">Successfully added:</p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {completedFiles.map((file, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                        {file.name} - {file.result?.status}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

        
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;